import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, getDocs, collection } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import { generatePhotoPdf } from '../utils/generatePhotoPdf';
import { generateFormPdf } from '../utils/generateFormPdf';
import { getReportItems, LEAD_ITEMS } from '../reportItems';
import { loadReport, applyToFormData, isConfirmed, getPanels, slotProblems, slotHasRepair } from '../reportSlots';
import { formKind } from '../projectFields';
import { rosterFromUser, buildSignerProfile } from '../disciplines';
import '../App.css';

const OVERALL_LABELS = {
  'passed':      '✓ Passed',
  'maintenance': '⚠ Maintenance',
  'repairs':     '🔧 Repairs Req.',
  'dangerous':   '⚡ Dangerous',
  'immediate':   '🚨 Immediate Danger',
  'no-repairs':  '✓ No Repairs',
};

const ReportGenerator = () => {
  const { projectId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [project, setProject]                       = useState(null);
  const [formData, setFormData]                     = useState({});
  const [panels, setPanels]                         = useState([]);
  const [overallResult, setOverallResult]           = useState('');
  const [repairsSummary, setRepairsSummary]         = useState('');
  const [additionalComments, setAdditionalComments] = useState('');
  const [reportType, setReportType]                 = useState('');
  const [recertType, setRecertType]                 = useState('');
  const [inspectionDate, setInspectionDate]         = useState('');
  const [photos, setPhotos]                         = useState([]);
  const [userProfile, setUserProfile]               = useState(null);   // chosen engineer + firm
  const [loading, setLoading]                       = useState(true);
  const [generatingForm, setGeneratingForm]         = useState(false);
  const [generatingPhotos, setGeneratingPhotos]     = useState(false);
  const [formReady, setFormReady]                   = useState(false);
  const [photosReady, setPhotosReady]               = useState(false);
  const [occupancyExplanation, setOccupancyExplanation] = useState('');
  const [repairPermit, setRepairPermit]             = useState('');
  const [reportSlots, setReportSlots]               = useState({});

  useEffect(() => {
    const load = async () => {
      try {
        const projectDoc = await getDoc(doc(db, 'projects', projectId));
        const proj = projectDoc.exists() ? projectDoc.data() : null;
        setProject(proj);

        const report = await loadReport(projectId, proj?.county);
        setReportSlots(report.slots);

        const formDoc = await getDoc(doc(db, 'projects', projectId, 'forms', 'electrical'));
        if (formDoc.exists()) {
          const d = formDoc.data();
          setFormData(d.sections || {});
          setPanels(d.panels || []);
          setOverallResult(d.overallResult || '');
          setRepairsSummary(d.repairsDescription || d.repairsSummary || '');
          setOccupancyExplanation(d.occupancyExplanation || '');
          setAdditionalComments(d.additionalComments || '');
          setReportType(d.reportType || '');
          setRecertType(d.recertType || '');
          setInspectionDate(d.inspectionDate || '');
          setRepairPermit(d.repairPermit || '');
        }
        const signer = formDoc.exists() ? formDoc.data() : {};

        const photosSnap = await getDocs(collection(db, 'projects', projectId, 'photos'));
        const allPhotos = photosSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setPhotos(allPhotos);

        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        const roster  = rosterFromUser(userDoc.exists() ? userDoc.data() : {});
        setUserProfile(buildSignerProfile(roster, signer.engineerId, signer.firmId));

      } catch (err) {
        console.error('Error loading report data:', err);
      }
      setLoading(false);
    };
    load();
  }, [projectId, currentUser]);

  // Photo Manager is the source of truth for item statuses and comments
  const mergedFormData = applyToFormData(formData, project?.county, reportSlots);
  const slotPanels     = getPanels(reportSlots);
  const pdfPanels      = slotPanels.length ? slotPanels : panels;
  const allSlotItems   = [...LEAD_ITEMS, ...getReportItems(project?.county)];
  const openSlots      = allSlotItems.filter(it =>
    !isConfirmed(reportSlots[it.key]) || (reportSlots[it.key] && slotProblems(reportSlots[it.key], it).length > 0)
  );
  const slotsReady     = openSlots.length === 0;
  // Repairs required Yes/No on the form comes from the Report Editor
  const repairsRequired = getReportItems(project?.county).some(it => slotHasRepair(reportSlots[it.key], it));
  const isBrowardForm   = formKind(project?.county) === 'broward';
  const conditionMissing = !isBrowardForm && !['passed', 'maintenance', 'dangerous', 'immediate'].includes(overallResult);

  const handleGenerateFormPdf = async () => {
    if (!slotsReady) return;
    setGeneratingForm(true);
    try {
      const blob = await generateFormPdf(
        mergedFormData, pdfPanels, overallResult, repairsSummary,
        project, userProfile, reportType, recertType, inspectionDate,
        additionalComments, occupancyExplanation, repairsRequired, repairPermit
      );
      const filename = `${project?.address || 'project'}_electrical_report.pdf`
        .replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      setFormReady(true);
    } catch (err) {
      console.error('Form PDF error:', err);
      alert('PDF generation failed: ' + err.message);
    }
    setGeneratingForm(false);
  };

  const handleGeneratePhotoPdf = async () => {
    if (!slotsReady) return;
    setGeneratingPhotos(true);
    try {
      const pdf = await generatePhotoPdf({ project, userProfile, slots: reportSlots, photos });
      const filename = `${project?.address || 'project'}_electrical_photos.pdf`
        .replace(/[^a-z0-9]/gi, '_').toLowerCase();
      pdf.save(filename);
      setPhotosReady(true);
    } catch (err) {
      console.error('Photo PDF error:', err);
      alert('Photo report failed: ' + err.message);
    }
    setGeneratingPhotos(false);
  };

  // Photo report contents, from the slots
  const slotPhotoCount = allSlotItems.reduce((n, it) => {
    const s = reportSlots[it.key];
    if (!s) return n;
    if (s.naReason) return n + 1;
    return n + Math.max(1, (s.photos || []).length);
  }, 0);
  const extraPhotoCount = allSlotItems.reduce((n, it) => n + Math.max(0, ((reportSlots[it.key]?.photos) || []).length - 1), 0);

  const sectionCount = Object.keys(mergedFormData).filter(k => mergedFormData[k]?.status).length;
  const repairCount  = Object.keys(mergedFormData).filter(k =>
    mergedFormData[k]?.status && !['good','yes','na','fair'].includes(mergedFormData[k]?.status)
  ).length;

  const overallLabel = isBrowardForm ? (repairsRequired ? 'Repairs required' : 'No repairs required') : (OVERALL_LABELS[overallResult] || '—');
  const overallColor = ['passed','no-repairs'].includes(overallResult) ? '#065f46' :
    overallResult === 'maintenance' ? '#92400e' :
    overallResult === 'repairs'     ? '#b91c1c' :
    ['dangerous','immediate'].includes(overallResult) ? '#7c2d12' : '#6b7280';

  if (loading) return <div style={{ marginLeft: '220px', padding: '2rem' }}>Loading...</div>;

  return (
    <div>
      <Sidebar />
      <div style={styles.container}>

        <div style={styles.titleRow}>
          <div>
            <h2 style={styles.title}>Generate Reports</h2>
            {project && (
              <p style={styles.subtitle}>
                {project.address}{project.city ? `, ${project.city}` : ''} — {project.county}
              </p>
            )}
          </div>
          <button style={styles.backButton} onClick={() => navigate(`/project/${projectId}/electrical`)}>
            ← Project Details
          </button>
        </div>

        {/* Summary card */}
        <div style={styles.summaryCard}>
          <h3 style={styles.sectionTitle}>Inspection Summary</h3>
          <div style={styles.summaryGrid}>
            <div style={styles.summaryItem}>
              <div style={styles.summaryValue}>{sectionCount}</div>
              <div style={styles.summaryLabel}>Sections Completed</div>
            </div>
            <div style={styles.summaryItem}>
              <div style={{ ...styles.summaryValue, color: repairCount > 0 ? '#b91c1c' : '#065f46' }}>
                {repairCount}
              </div>
              <div style={styles.summaryLabel}>Sections Requiring Repairs</div>
            </div>
            <div style={styles.summaryItem}>
              <div style={styles.summaryValue}>{allSlotItems.length - openSlots.length} / {allSlotItems.length}</div>
              <div style={styles.summaryLabel}>Slots Confirmed</div>
            </div>
            <div style={styles.summaryItem}>
              <div style={styles.summaryValue}>{extraPhotoCount}</div>
              <div style={styles.summaryLabel}>Additional Repair Photos</div>
            </div>
            <div style={styles.summaryItem}>
              <div style={{ ...styles.summaryValue, fontSize: '1rem', color: overallColor }}>
                {overallLabel}
              </div>
              <div style={styles.summaryLabel}>Overall Result</div>
            </div>
          </div>
        </div>

        {/* Report Editor gate */}
        {!slotsReady && (
          <div style={styles.warning}>
            ⚠ {openSlots.length} Report Editor slot{openSlots.length !== 1 ? 's' : ''} not confirmed or needing attention ({openSlots.map(it => it.photoLabel).join(', ')}). The form PDF unlocks when every slot is confirmed and complete.{' '}
            <span style={styles.warningLink} onClick={() => navigate(`/project/${projectId}/photos`)}>Open Report Editor</span>
          </div>
        )}

        {/* Missing info warnings */}
        {(!userProfile?.hasEngineer || !userProfile?.hasFirm) && (
          <div style={styles.warning}>
            ⚠ {!userProfile?.hasEngineer && !userProfile?.hasFirm ? 'No engineer or firm selected' : !userProfile?.hasEngineer ? 'No engineer selected' : 'No firm selected'} — the signature and firm block will print blank.{' '}
            <span style={styles.warningLink} onClick={() => navigate(`/project/${projectId}/electrical`)}>Choose in Project Details</span>
          </div>
        )}
        {userProfile?.hasFirm && !userProfile?.logoUrl && (
          <div style={styles.warning}>
            ⚠ {userProfile.firmName || 'The selected firm'} has no logo. The photo report footer will show the firm name only.{' '}
            <span style={styles.warningLink} onClick={() => navigate('/settings')}>Upload Logo</span>
          </div>
        )}
        {conditionMissing && (
          <div style={styles.warning}>
            ⚠ Overall inspection result not selected — page 1 condition boxes will print blank.{' '}
            <span style={styles.warningLink} onClick={() => navigate(`/project/${projectId}/electrical`)}>Choose in Project Details</span>
          </div>
        )}
        {!reportType && (
          <div style={styles.warning}>
            ⚠ Report type not set (Initial / Amended).{' '}
            <span style={styles.warningLink} onClick={() => navigate(`/project/${projectId}/electrical`)}>
              Update Form
            </span>
          </div>
        )}

        {/* PDF generation cards */}
        <div style={styles.pdfGrid}>

          {/* Form PDF */}
          <div style={styles.pdfCard}>
            <div style={styles.pdfCardIcon}>📋</div>
            <h3 style={styles.pdfCardTitle}>Electrical Inspection Report</h3>
            <p style={styles.pdfCardDesc}>
              Generates the completed inspection form with all section statuses,
              comments, panel data, overall result, and engineer signature block.
            </p>
            <div style={styles.pdfCardMeta}>
              <span>Jurisdiction: {project?.county || '—'}</span>
              <span>Report Type: {reportType || '—'}</span>
              <span>Recertification: {recertType || '—'}</span>
              <span>Date: {inspectionDate || '—'}</span>
              <span>Overall: {overallLabel}</span>
              {additionalComments && (
                <span style={{ color: '#16a34a' }}>✓ Additional comments included</span>
              )}
            </div>
            <button
              style={{ ...styles.generateButton, backgroundColor: !slotsReady ? '#9ca3af' : formReady ? '#065f46' : '#112b49', cursor: slotsReady ? 'pointer' : 'not-allowed' }}
              onClick={handleGenerateFormPdf}
              disabled={generatingForm || !slotsReady}
            >
              {!slotsReady ? 'Confirm all Report Editor slots first' :
                generatingForm ? 'Generating...' :
                formReady ? '✓ Downloaded — Generate Again' : 'Generate Form PDF'}
            </button>
          </div>

          {/* Photo PDF */}
          <div style={styles.pdfCard}>
            <div style={styles.pdfCardIcon}>📷</div>
            <h3 style={styles.pdfCardTitle}>Photo Report</h3>
            <p style={styles.pdfCardDesc}>
              One photo per form item in form order (PHOTO-01, 02, 03…), then the repair photos
              numbered on from the last item. Four photos per page, captions below. Address
              header, firm logo footer and page numbers.
            </p>
            <div style={styles.pdfCardMeta}>
              <span>Photos and panels: {slotPhotoCount}</span>
              <span>Additional repair photos: {extraPhotoCount}</span>
              <span>Est. pages: {Math.ceil(slotPhotoCount / 4) || 0}</span>
            </div>
            <button
              style={{
                ...styles.generateButton,
                backgroundColor: !slotsReady ? '#9ca3af' : photosReady ? '#065f46' : '#112b49',
                cursor: slotsReady ? 'pointer' : 'not-allowed',
              }}
              onClick={handleGeneratePhotoPdf}
              disabled={generatingPhotos || !slotsReady}
            >
              {!slotsReady ? 'Confirm all Report Editor slots first' :
                generatingPhotos ? 'Generating...' :
                photosReady ? '✓ Downloaded — Generate Again' :
                'Generate Photo PDF'}
            </button>
          </div>

        </div>
      </div>
      <Footer />
    </div>
  );
};

const styles = {
  container:     { marginLeft: '200px', padding: '2rem', paddingBottom: '5rem' },
  titleRow:      { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' },
  title:         { margin: '0 0 0.25rem', color: '#112b49', fontSize: '1.5rem' },
  subtitle:      { margin: 0, color: '#555', fontSize: '0.9rem' },
  backButton:    { padding: '0.5rem 1.25rem', backgroundColor: '#6b7280', color: 'white', border: 'none', borderRadius: '5px', fontSize: '0.95rem', cursor: 'pointer', fontFamily: 'inherit' },
  summaryCard:   { backgroundColor: 'rgba(255,255,255,0.92)', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '1rem' },
  sectionTitle:  { margin: '0 0 1rem', fontSize: '1rem', fontWeight: '600', color: '#112b49', textTransform: 'uppercase', letterSpacing: '0.05em' },
  summaryGrid:   { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '1rem' },
  summaryItem:   { textAlign: 'center' },
  summaryValue:  { fontSize: '1.75rem', fontWeight: '700', color: '#112b49', lineHeight: 1, marginBottom: '0.25rem' },
  summaryLabel:  { fontSize: '0.75rem', color: '#6b7280' },
  warning:       { backgroundColor: '#fef9c3', border: '1px solid #fde047', borderRadius: '6px', padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#713f12', marginBottom: '0.75rem' },
  warningLink:   { color: 'cornflowerblue', cursor: 'pointer', fontWeight: '600', textDecoration: 'underline' },
  pdfGrid:       { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.25rem', marginTop: '1rem' },
  pdfCard:       { backgroundColor: 'rgba(255,255,255,0.92)', borderRadius: '8px', padding: '1.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  pdfCardIcon:   { fontSize: '2rem' },
  pdfCardTitle:  { margin: 0, color: '#112b49', fontSize: '1.1rem', fontWeight: '700' },
  pdfCardDesc:   { margin: 0, color: '#555', fontSize: '0.875rem', lineHeight: '1.5' },
  pdfCardMeta:   { display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.8rem', color: '#6b7280', backgroundColor: '#f9fafb', padding: '0.75rem', borderRadius: '4px' },
  generateButton:{ padding: '0.75rem', color: 'white', border: 'none', borderRadius: '5px', fontSize: '1rem', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', transition: 'background-color 0.2s', marginTop: 'auto' },
  noPhotosHint:  { margin: 0, fontSize: '0.8rem', textAlign: 'center' },
};

export default ReportGenerator;